/**
 * Lista de expedientes del municipio activo (Sprint 5).
 *
 * Consume `GET /projects?municipality=DANE` y pinta una tabla simple con
 * link al detalle. Estado loading/empty/error inline.
 *
 * Asistente Formulador (v1.1.0): boton "Formular desde una idea" abre
 * IdeaEntryDialog para crear un expediente idea-first.
 */
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { IdeaEntryDialog } from "@/components/formulador/IdeaEntryDialog";
import { ProjectStatusBadge } from "@/components/ProjectStatusBadge";
import { ApiError, fetchProjects, type ProjectDTO } from "@/lib/api";
import { useActiveMunicipalityDane } from "@/lib/municipality";
import { formatCOP } from "@/lib/utils";

import { etiquetaSector } from "./expediente/_shared";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

// Issue #882: el backend corta en 50 por defecto y no manda el total, asi que
// la lista pagina con "Ver más". Se pide uno de mas para saber si queda otra
// pagina sin ofrecer un "Ver más" que traiga nada.
const PAGE_SIZE = 50;

export function Expedientes() {
  const activeDane = useActiveMunicipalityDane();
  const [showIdeaDialog, setShowIdeaDialog] = useState(false);

  const {
    data: pages,
    isLoading,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useInfiniteQuery({
    queryKey: ["projects", activeDane],
    queryFn: async ({ pageParam }) => {
      const items = await fetchProjects(activeDane, { limit: PAGE_SIZE + 1, offset: pageParam });
      return { items: items.slice(0, PAGE_SIZE), hasMore: items.length > PAGE_SIZE };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, all) => (lastPage.hasMore ? all.length * PAGE_SIZE : undefined),
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return count < 2;
    },
  });
  // Si un expediente nuevo corre el orden entre una pagina y otra, el que se
  // repite se pinta una sola vez (el id es la key de la fila).
  const data = pages
    ? Array.from(new Map(pages.pages.flatMap((page) => page.items).map((p) => [p.id, p])).values())
    : undefined;

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Expedientes</h1>
          <p className="mt-1 text-sm text-slate-600">
            Proyectos del municipio: radicación, documentos, seguimiento.
          </p>
        </div>
        {/* Asistente Formulador: entrada idea-first */}
        <button
          type="button"
          data-testid="formular-desde-idea-btn"
          onClick={() => setShowIdeaDialog(true)}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-cta-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-600 focus:ring-2 focus:ring-cta-500 focus:ring-offset-2 focus:outline-hidden"
        >
          <svg aria-hidden="true" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
          </svg>
          Formular desde una idea
        </button>
      </header>

      {showIdeaDialog && (
        <IdeaEntryDialog municipality={activeDane} onClose={() => setShowIdeaDialog(false)} />
      )}

      {isLoading && <Skeleton />}

      {/* Si lo que falla es "Ver más", las filas ya pintadas siguen y el
          aviso va junto al botón, no aquí. */}
      {isError && !isFetchNextPageError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
          Error cargando expedientes: {error instanceof Error ? error.message : "desconocido"}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
          <p className="mb-3">Aún no hay expedientes.</p>
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link
              to="/radar"
              className="inline-flex items-center gap-1 text-sm font-semibold text-brand-700 hover:underline"
            >
              Crear uno desde el Radar →
            </Link>
            <span className="hidden text-slate-300 sm:inline">|</span>
            <button
              type="button"
              onClick={() => setShowIdeaDialog(true)}
              className="inline-flex items-center gap-1 text-sm font-semibold text-cta-600 hover:underline"
            >
              Formular desde una idea →
            </button>
          </div>
        </div>
      )}

      {data && data.length > 0 && (
        <ul
          data-testid="projects-list"
          className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs"
        >
          {data.map((p) => (
            <ProjectRow key={p.id} project={p} />
          ))}
        </ul>
      )}

      {data && data.length > 0 && hasNextPage && (
        <div
          data-testid="projects-paginacion"
          className="mt-6 flex flex-col items-center gap-2 text-sm text-slate-600"
        >
          <p>Ves los {data.length} expedientes más recientes. Hay más antiguos.</p>
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="rounded-md border border-brand-700 bg-white px-4 py-2 text-sm font-semibold text-brand-700 hover:bg-brand-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700 disabled:cursor-wait disabled:opacity-60"
          >
            {isFetchingNextPage ? "Cargando expedientes…" : "Ver más expedientes"}
          </button>
          {isFetchNextPageError && !isFetchingNextPage && (
            <p role="alert" className="text-red-700">
              No pudimos cargar más expedientes. Revisa tu conexión y vuelve a pulsar «Ver más
              expedientes».
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ProjectRow({ project }: { project: ProjectDTO }) {
  return (
    <li>
      <Link
        to={`/expedientes/${project.id}`}
        className="flex flex-col gap-2 px-5 py-4 transition hover:bg-slate-50 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <ProjectStatusBadge status={project.status} />
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-slate-700 uppercase">
              {etiquetaSector(project.sector)}
            </span>
            {/* Badge idea-first: solo mientras sigue en borrador. Radicado,
                rechazado o cerrado ya no esta "en formulación" (issue #881). */}
            {project.opportunity_id === null && project.status === "draft" && (
              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-brand-700 uppercase">
                En formulación
              </span>
            )}
          </div>
          <p className="truncate text-sm font-semibold text-slate-900">{project.name}</p>
          {project.opportunity_title && (
            <p className="truncate text-xs text-slate-500">Desde: {project.opportunity_title}</p>
          )}
        </div>
        <div className="text-right text-xs text-slate-500">
          <p className="font-mono text-sm text-slate-900">
            {formatCOP(project.amount_requested_cop)}
          </p>
          <p>Creado {formatDate(project.created_at)}</p>
        </div>
      </Link>
    </li>
  );
}

function Skeleton() {
  return (
    <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="h-20 animate-pulse bg-slate-50" />
      ))}
    </ul>
  );
}
